/**
 * RTK Query API Definition with Entity Adapters
 *
 * This replaces the old async thunk pattern with RTK Query for data fetching.
 * RTK Query provides:
 * - Automatic caching with configurable cache times
 * - Auto-generated hooks for components
 * - Loading/error states built-in
 * - Request deduplication
 * - Cache invalidation via tags
 *
 * Entity Adapters normalize collections by ID for optimized lookups:
 * - O(1) lookups by ID instead of O(n) array operations
 * - Memoized selectors prevent unnecessary re-renders
 * - Consistent data structure across the app
 *
 * Usage in components:
 *   const { data, isLoading, error } = useGetLastIndividualCompQuery();
 *   const { data: events } = useGetUpcomingEventsQuery();
 *   const { data: comp } = useGetIndividualCompQuery(compID);
 *
 *   // With entity adapters:
 *   const member = useAppSelector(state => selectMemberById(state, memberId));
 *   const allMembers = useAppSelector(selectAllMembers);
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createEntityAdapter } from '@reduxjs/toolkit';
import type { EntityState } from '@reduxjs/toolkit';
import { apiRoot } from '../config';
import type {
  MemberSummary,
  GolfEventSummary,
  TeamCompetition,
  IndividualCompetition,
  IndividualCompetitionResult,
  IndividualCompetitorResult,
  TeamSessionSummary,
  TeamCompetitorMemberSummary,
  TeamCompetitorCompSummary,
  TeamCompetitorFormatSummary,
  TeamMatchPlayResults,
  MemberHandicap,
  MemberHandicapsResponse,
  PublicDocumentsResponse,
  UpcomingEvent,
  UpcomingEventsResponse,
  RyderCupTeamsResponse,
  CurrentRyderCupTeamMember,
  CommitteeMember,
  CommitteeMembersResponse,
} from '../types/api.types';

// Default cache time: 5 minutes (matches the old cacheUtils behavior)
const DEFAULT_CACHE_TIME = 5 * 60;

// ===========================
// Entity Adapters
// ===========================
// Entity adapters normalize collections by ID for O(1) lookups
// They provide selectors like selectAll, selectById, selectIds, selectEntities, selectTotal

/**
 * Member Summary entity adapter
 * Normalizes member summary data by ID for efficient lookups
 * Members are kept in the order returned by the API
 */
export const membersAdapter = createEntityAdapter<MemberSummary, string>({
  selectId: (member) => member.id,
});

/**
 * Golf Event Summary entity adapter
 * Normalizes event summary data by ID, sorted by date (most recent first)
 */
export const eventsAdapter = createEntityAdapter<GolfEventSummary, string>({
  selectId: (event) => event.id,
  sortComparer: (a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
  },
});

/**
 * Team Competition entity adapter
 * Normalizes team competition data by ID
 */
export const teamCompetitionsAdapter = createEntityAdapter<TeamCompetition, string>({
  selectId: (comp) => comp.id,
  sortComparer: (a, b) => {
    const dateA = new Date(a.eventDate);
    const dateB = new Date(b.eventDate);
    return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
  },
});

/**
 * Individual Competition entity adapter
 * Normalizes individual competition data by ID
 */
export const individualCompetitionsAdapter = createEntityAdapter<IndividualCompetition, string>({
  selectId: (comp) => comp.id,
});

// Export entity selectors for use in components
export const memberSelectors = membersAdapter.getSelectors();
export const eventSelectors = eventsAdapter.getSelectors();
export const teamCompetitionSelectors = teamCompetitionsAdapter.getSelectors();
export const individualCompetitionSelectors = individualCompetitionsAdapter.getSelectors();

// ===========================
// Endpoint Builder Functions
// ===========================
// These functions define endpoint groups to keep the main endpoints builder under the max-lines-per-function limit

import type { EndpointBuilder } from '@reduxjs/toolkit/query/react';

type Builder = EndpointBuilder<ReturnType<typeof fetchBaseQuery>, string, 'api'>;

/**
 * Individual Competition endpoints
 */
const buildIndividualCompetitionEndpoints = (builder: Builder) => ({
  getLastIndividualComp: builder.query<IndividualCompetition, void>({
    query: () => 'IndividualCompetitions/Latest?withResults=true&series=Crazy+English+Tournament',
    providesTags: ['IndividualCompetition'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getIndividualComp: builder.query<IndividualCompetition, string>({
    query: (compID) => `IndividualCompetitions/${compID}?withResults=true`,
    providesTags: (_result, _error, compID) => [{ type: 'IndividualCompetition', id: compID }],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getIndividualResult: builder.query<IndividualCompetitionResult[], string>({
    query: (compID) => `IndividualCompetitions/${compID}/Results`,
    providesTags: (_result, _error, compID) => [{ type: 'IndividualCompetition', id: compID }],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getMemberIndividualResults: builder.query<IndividualCompetitorResult[], string>({
    query: (memberID) => `IndividualCompetitions/Results/ByMember?memberID=${memberID}`,
    providesTags: (_result, _error, memberID) => [{ type: 'Member', id: memberID }],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),
});

/**
 * Team Competition endpoints
 */
const buildTeamCompetitionEndpoints = (builder: Builder) => ({
  getLastTeamComp: builder.query<TeamCompetition, void>({
    query: () => 'TeamCompetitions/Latest',
    providesTags: ['TeamCompetition'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getTeamCompetitions: builder.query<EntityState<TeamCompetition, string>, { withCompetitors?: boolean } | void>({
    query: (arg) => {
      const withCompetitors = arg && typeof arg === 'object' ? arg.withCompetitors : undefined;
      return withCompetitors ? 'TeamCompetitions?withCompetitors=true' : 'TeamCompetitions';
    },
    providesTags: ['TeamCompetition'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
    transformResponse: (response: TeamCompetition[]) => {
      return teamCompetitionsAdapter.setAll(teamCompetitionsAdapter.getInitialState(), response);
    },
  }),

  getCompTeamResultsBySession: builder.query<TeamSessionSummary[], string>({
    query: (compID) => `TeamCompetitions/${compID}/Results/BySession`,
    providesTags: (_result, _error, compID) => [{ type: 'TeamCompetition', id: compID }],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getEventTeamResultsBySession: builder.query<TeamSessionSummary[], string>({
    query: (compID) => `TeamCompetitions/${compID}/Results/BySession`,
    providesTags: (_result, _error, compID) => [{ type: 'TeamCompetition', id: compID }],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getEventTeamResultsByMember: builder.query<TeamCompetitorMemberSummary[], string>({
    query: (compID) => `TeamCompetitions/${compID}/Results/ByMember`,
    providesTags: (_result, _error, compID) => [{ type: 'TeamCompetition', id: compID }],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getMemberTeamResultsByComp: builder.query<TeamCompetitorCompSummary[], string>({
    query: (memberID) => `TeamCompetitions/Results/ByCompetition?memberID=${memberID}`,
    providesTags: (_result, _error, memberID) => [{ type: 'Member', id: memberID }],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getMemberTeamResultsByFormat: builder.query<TeamCompetitorFormatSummary[], string>({
    query: (memberID) => `TeamCompetitions/Results/ByFormat?memberID=${memberID}`,
    providesTags: (_result, _error, memberID) => [{ type: 'Member', id: memberID }],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getTeamResultsByFormatMember: builder.query<TeamCompetitorFormatSummary[], void>({
    query: () => 'TeamCompetitions/Results/ByFormat',
    providesTags: ['TeamCompetition', 'Member'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getSinglesStrokeResults: builder.query<TeamMatchPlayResults[], void>({
    query: () => 'TeamCompetitions/Results/MatchPlay/SinglesStroke',
    providesTags: ['TeamCompetition'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),
});

// Transform function for member handicaps
export const transformMemberHandicapsResponse = (response: MemberHandicapsResponse): MemberHandicap[] => {
  if (!response.values || response.values.length <= 1) {
    return [];
  }

  return response.values
    .slice(1) // Skip header row
    .map((row: (string | number | undefined)[]): MemberHandicap | null => {
      const id = row[0]?.toString();
      const firstName = row[1]?.toString();
      const lastName = row[2]?.toString();
      const handicap = row[3];

      if (!id || !firstName || !lastName || handicap === undefined) return null;

      return {
        id,
        firstName,
        lastName,
        handicap: typeof handicap === 'number' ? handicap : parseFloat(handicap.toString()),
      };
    })
    .filter((handicap: MemberHandicap | null): handicap is MemberHandicap => handicap !== null);
};

/**
 * Member endpoints
 */
const buildMemberEndpoints = (builder: Builder) => ({
  getMemberSummary: builder.query<EntityState<MemberSummary, string>, void>({
    query: () => 'Members/Summary',
    providesTags: ['Member'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
    transformResponse: (response: MemberSummary[]) => {
      return membersAdapter.setAll(membersAdapter.getInitialState(), response);
    },
  }),

  getMemberHandicaps: builder.query<MemberHandicap[], void>({
    query: () => 'google/member-handicaps',
    providesTags: ['Handicap'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
    transformResponse: transformMemberHandicapsResponse,
  }),
});

/**
 * Event endpoints
 */
const buildEventEndpoints = (builder: Builder) => ({
  getGolfEventSummary: builder.query<EntityState<GolfEventSummary, string>, void>({
    query: () => 'Events/Summary',
    providesTags: ['Event'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
    transformResponse: (response: GolfEventSummary[]) => {
      return eventsAdapter.setAll(eventsAdapter.getInitialState(), response);
    },
  }),

  getGolfEvent: builder.query<GolfEventSummary[], string>({
    query: (eventID) => `Events?eventID=${eventID}`,
    providesTags: (_result, _error, eventID) => [{ type: 'Event', id: eventID }],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getLastGolfEvent: builder.query<GolfEventSummary, void>({
    query: () => 'Events/Latest',
    providesTags: ['Event'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),
});

// Transform function for upcoming events
export const transformUpcomingEventsResponse = (response: UpcomingEventsResponse): UpcomingEvent[] => {
  if (!response.values || response.values.length <= 1) {
    return [];
  }

  return response.values
    .slice(1) // Skip header row
    .map((row: (string | undefined)[]): UpcomingEvent | null => {
      const date = row[0];
      const eventType = row[1];
      const organiser1 = row[2];
      const organiser2 = row[3];
      const venue = row[4];
      const url = row[5];
      const image = row[6];
      const details = row[7];

      if (!date || !eventType) return null;

      return {
        date,
        eventType,
        organiser1,
        organiser2,
        venue,
        url,
        image,
        details,
      };
    })
    .filter((event: UpcomingEvent | null): event is UpcomingEvent => event !== null);
};

// Transform function for Ryder Cup teams
export const transformRyderCupTeamsResponse = (response: RyderCupTeamsResponse): CurrentRyderCupTeamMember[] => {
  if (!response.values || response.values.length <= 1) {
    return [];
  }

  return response.values
    .slice(1) // Skip header row
    .map((row): CurrentRyderCupTeamMember | null => {
      const team = row[0] as 'USA' | 'EU' | undefined;
      const id = row[1];
      const captain = row[2] as 'Yes' | 'No' | undefined;

      if (!team || !id) return null;

      return {
        team,
        id,
        captain,
      };
    })
    .filter((member): member is CurrentRyderCupTeamMember => member !== null);
};

// Transform function for committee members
export const transformCommitteeMembersResponse = (response: CommitteeMembersResponse): CommitteeMember[] => {
  if (!response.values || response.values.length <= 1) {
    return [];
  }

  return response.values
    .slice(1) // Skip header row
    .map((row: (string | undefined)[]): CommitteeMember | null => {
      const season = row[0];
      const chairman = row[1];
      const secretary = row[2];
      const treasurer = row[3];
      const captain = row[4];
      const committeeMember1 = row[5];
      const committeeMember2 = row[6];

      if (!season || !chairman || !secretary || !treasurer || !captain) {
        return null;
      }

      return {
        season,
        chairman,
        secretary,
        treasurer,
        captain,
        committeeMember1: committeeMember1 || undefined,
        committeeMember2: committeeMember2 || undefined,
      };
    })
    .filter((member: CommitteeMember | null): member is CommitteeMember => member !== null);
};

/**
 * Google API endpoints (documents, events, Ryder Cup, committee)
 */
const buildGoogleApiEndpoints = (builder: Builder) => ({
  getPublicDocuments: builder.query<PublicDocumentsResponse, void>({
    query: () => 'google/public-documents',
    providesTags: ['Document'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
  }),

  getUpcomingEvents: builder.query<UpcomingEvent[], void>({
    query: () => 'google/upcoming-events',
    providesTags: ['UpcomingEvent'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
    transformResponse: transformUpcomingEventsResponse,
  }),

  getLatestRyderCupTeams: builder.query<CurrentRyderCupTeamMember[], void>({
    query: () => 'google/ryder-cup-teams',
    providesTags: ['RyderCupTeam'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
    transformResponse: transformRyderCupTeamsResponse,
  }),

  getCommitteeMembers: builder.query<CommitteeMember[], void>({
    query: () => 'google/committee-members',
    providesTags: ['CommitteeMember'],
    keepUnusedDataFor: DEFAULT_CACHE_TIME,
    transformResponse: transformCommitteeMembersResponse,
  }),
});

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: `${apiRoot}/api`,
    // Global error handling
    validateStatus: (response, _result) => {
      if (!response.ok) {
        console.error('Bad Response: ', response.statusText);
        return false;
      }
      return true;
    },
  }),
  // Tags for cache invalidation
  tagTypes: [
    'IndividualCompetition',
    'TeamCompetition',
    'Member',
    'Event',
    'Handicap',
    'Document',
    'UpcomingEvent',
    'RyderCupTeam',
    'CommitteeMember',
  ],
  endpoints: (builder) => ({
    ...buildIndividualCompetitionEndpoints(builder),
    ...buildTeamCompetitionEndpoints(builder),
    ...buildMemberEndpoints(builder),
    ...buildEventEndpoints(builder),
    ...buildGoogleApiEndpoints(builder),
  }),
});

// Export auto-generated hooks
export const {
  // Individual Competitions
  useGetLastIndividualCompQuery,
  useGetIndividualCompQuery,
  useGetIndividualResultQuery,
  useGetMemberIndividualResultsQuery,

  // Team Competitions
  useGetLastTeamCompQuery,
  useGetTeamCompetitionsQuery,
  useGetCompTeamResultsBySessionQuery,
  useGetEventTeamResultsBySessionQuery,
  useGetEventTeamResultsByMemberQuery,
  useGetMemberTeamResultsByCompQuery,
  useGetMemberTeamResultsByFormatQuery,
  useGetTeamResultsByFormatMemberQuery,
  useGetSinglesStrokeResultsQuery,

  // Members
  useGetMemberSummaryQuery,
  useGetMemberHandicapsQuery,

  // Events
  useGetGolfEventSummaryQuery,
  useGetGolfEventQuery,
  useGetLastGolfEventQuery,

  // Documents and Events
  useGetPublicDocumentsQuery,
  useGetUpcomingEventsQuery,

  // Ryder Cup
  useGetLatestRyderCupTeamsQuery,

  // Committee
  useGetCommitteeMembersQuery,
} = api;
